import React, { useEffect, useRef, useState } from 'react'
import './FilterBarSelect.scss'

/**
 * FilterBarSelect - Componente select customizado totalmente estilizado e controlável
 * Props:
 * - options: [{ value, label }]
 * - value: valor selecionado
 * - onChange: função chamada ao selecionar
 * - placeholder: texto do placeholder
 * - autoOpen: se true, abre o dropdown automaticamente ao montar
 * - className: classe extra para o wrapper
 * - dropdownClassName: classe extra para o dropdown
 */
export default function FilterBarSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Selecione...',
  autoOpen = false,
  className = '',
  dropdownClassName = '',
  enableSearch = true,
  searchPlaceholder = 'Type to search...',
  onClose
}) {
  const [open, setOpen] = useState(autoOpen)
  const [highlight, setHighlight] = useState(-1)
  const [search, setSearch] = useState('')
  const wrapperRef = useRef(null)
  const listRef = useRef(null)
  const searchInputRef = useRef(null)

  // Abre automaticamente ao montar se autoOpen
  useEffect(() => {
    if (autoOpen) setOpen(true)
  }, [autoOpen])

  // Fecha ao clicar fora
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        if (open) {
          setOpen(false)
          onClose && onClose()
        }
      }
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open, onClose])

  // Lista filtrada
  const filtered = enableSearch && search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options

  // Reset highlight quando muda filtered
  useEffect(() => { setHighlight(filtered.length ? 0 : -1) }, [search, options, filtered.length])

  // Focar input search ao abrir
  useEffect(() => {
    if (open && enableSearch && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [open, enableSearch])

  // Navegação teclado
  useEffect(() => {
    if (!open) return
    function handleKey(e) {
      if (e.key === 'ArrowDown') {
        setHighlight(h => Math.min(h + 1, filtered.length - 1))
      } else if (e.key === 'ArrowUp') {
        setHighlight(h => Math.max(h - 1, 0))
      } else if (e.key === 'Enter' && highlight >= 0 && filtered[highlight]) {
        onChange && onChange(filtered[highlight].value)
        setOpen(false)
        onClose && onClose()
      } else if (e.key === 'Escape') {
        setOpen(false)
        onClose && onClose()
      } else if (enableSearch && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        // Digitação direta quando o campo de busca não está focado
        if (document.activeElement !== searchInputRef.current) {
          setSearch(prev => prev + e.key)
        }
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, highlight, filtered, onChange, enableSearch, onClose])

  // Abre dropdown ao focar
  function handleFocus() {
    setOpen(true)
  }

  // Seleciona opção
  function handleSelect(val) {
    onChange && onChange(val)
    setOpen(false)
    onClose && onClose()
  }

  // Label do selecionado
  const selectedLabel = options.find(o => o.value === value)?.label || ''

  return (
    <div className={`filterbar-select-wrapper ${className}`} ref={wrapperRef}>
      <div
        className={`filterbar-select-display${open ? ' open' : ''}`}
        tabIndex={0}
        onClick={() => {
          setOpen(o => {
            const next = !o
            if (!next) onClose && onClose()
            return next
          })
        }}
        onFocus={handleFocus}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selectedLabel || <span className="filterbar-select-placeholder">{placeholder}</span>}
      </div>
      {open && (
        <div className={`filterbar-select-dropdown ${dropdownClassName}`} role="listbox">
          {enableSearch && (
            <div className="filterbar-select-search-wrapper">
              <input
                ref={searchInputRef}
                className="filterbar-select-search"
                placeholder={searchPlaceholder}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && highlight >= 0 && filtered[highlight]) { handleSelect(filtered[highlight].value) } }}
              />
            </div>
          )}
          <ul ref={listRef} className="filterbar-select-options" role="presentation">
            {filtered.map((opt, i) => (
              <li
                key={opt.value}
                className={`filterbar-select-option${value === opt.value ? ' selected' : ''}${highlight === i ? ' highlight' : ''}`}
                onMouseDown={() => handleSelect(opt.value)}
                role="option"
                aria-selected={value === opt.value}
              >
                {opt.label}
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="filterbar-select-no-results">No results</li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
